import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  ListChecks, Bell, Plus, Check, Trash2, ChevronRight, CircleDot, Sparkles,
} from "lucide-react";

const PRIO = {
  haute: { label: "Haute", cls: "bg-red-500/20 text-red-300 border-red-500/40" },
  moyenne: { label: "Moyenne", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  basse: { label: "Basse", cls: "bg-zinc-600/30 text-zinc-300 border-zinc-600/50" },
};
const inputCls = "w-full bg-black/40 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none";

export default function TasksCenterModule() {
  const [data, setData] = useState(null);
  const [nt, setNt] = useState({ titre: "", priorite: "moyenne", lien: "" });
  const nav = useNavigate();

  const load = () => api.get("/tasks/overview").then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-zinc-500 p-8">Chargement…</div>;
  const { tasks, notifications, counts } = data;

  const addTask = async () => {
    if (!nt.titre.trim()) return toast.error("Donne un titre à la tâche.");
    await api.post("/tasks", nt); setNt({ titre: "", priorite: "moyenne", lien: "" });
    toast.success("Tâche ajoutée"); load();
  };
  const toggle = async (t) => { await api.put(`/tasks/${t.id}`, { statut: t.statut === "fait" ? "a_faire" : "fait" }); load(); };
  const setPrio = async (t, p) => { await api.put(`/tasks/${t.id}`, { priorite: p }); load(); };
  const del = async (id) => { await api.delete(`/tasks/${id}`); load(); };
  const seed = async () => { const r = await api.post("/tasks/seed-defaults"); toast.success(`${r.data.created} tâche(s) de rentrée ajoutée(s)`); load(); };

  return (
    <div className="space-y-6" data-testid="tasks-center-module">
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2 flex items-center gap-2"><ListChecks size={13} /> CENTRE DE TÂCHES & NOTIFICATIONS</div>
        <h1 className="text-3xl font-bold tracking-tight">À faire · Rappels · Notifications</h1>
        <p className="text-zinc-500 text-sm mt-2">Reste à jour sur tout ce qui est important, classé par priorité.</p>
        <div className="grid grid-cols-3 gap-3 mt-5 max-w-md">
          {[["À faire", counts.a_faire], ["Faites", counts.fait], ["Notifications", counts.notifications]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-zinc-800 bg-black/40 p-3 text-center"><div className="text-2xl font-bold text-amber-400">{v}</div><div className="text-[10px] uppercase text-zinc-500 tracking-wider">{l}</div></div>
          ))}
        </div>
      </div>

      {/* Notifications cliquables */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-5" data-testid="notifications-panel">
        <div className="flex items-center gap-2 text-sm font-semibold mb-4"><Bell size={16} className="text-amber-400" /> Notifications ({notifications.length})</div>
        {notifications.length === 0 ? (
          <div className="text-sm text-zinc-600 italic">✅ Rien d'urgent — tout est à jour !</div>
        ) : (
          <div className="space-y-2">
            {notifications.map((n) => (
              <button key={n.id} onClick={() => nav(n.lien)} data-testid={`notif-${n.id}`}
                className={`w-full text-left flex items-center justify-between gap-3 rounded-xl border p-3.5 transition-colors hover:bg-white/[0.04] ${n.niveau === "haute" ? "border-red-500/40 bg-red-500/[0.05]" : "border-amber-500/30 bg-amber-500/[0.04]"}`}>
                <div className="flex items-center gap-2.5">
                  <CircleDot size={15} className={n.niveau === "haute" ? "text-red-400" : "text-amber-400"} />
                  <span className="text-sm text-zinc-200">{n.texte}</span>
                </div>
                <ChevronRight size={16} className="text-zinc-500 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Ajouter une tâche */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-5" data-testid="add-task">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold"><Plus size={16} className="text-amber-400" /> Nouvelle tâche</div>
          <button onClick={seed} className="text-xs inline-flex items-center gap-1.5 text-amber-300 hover:underline" data-testid="seed-tasks-btn"><Sparkles size={13} /> Ajouter les tâches de rentrée</button>
        </div>
        <div className="grid sm:grid-cols-6 gap-2">
          <input className={`${inputCls} sm:col-span-3`} placeholder="Titre de la tâche" value={nt.titre} onChange={(e) => setNt({ ...nt, titre: e.target.value })} data-testid="task-titre" />
          <select className={inputCls} value={nt.priorite} onChange={(e) => setNt({ ...nt, priorite: e.target.value })} data-testid="task-priorite">
            <option value="haute">Haute</option><option value="moyenne">Moyenne</option><option value="basse">Basse</option>
          </select>
          <input className={inputCls} placeholder="Lien (ex /app/inscriptions)" value={nt.lien} onChange={(e) => setNt({ ...nt, lien: e.target.value })} data-testid="task-lien" />
          <button onClick={addTask} className="bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold rounded-lg px-3" data-testid="task-add-btn">Ajouter</button>
        </div>
      </div>

      {/* Liste des tâches */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-5" data-testid="tasks-list">
        <div className="flex items-center gap-2 text-sm font-semibold mb-4"><ListChecks size={16} className="text-amber-400" /> Mes tâches (classées par priorité)</div>
        {tasks.length === 0 ? (
          <div className="text-sm text-zinc-600 italic">Aucune tâche. Clique « Ajouter les tâches de rentrée » pour démarrer.</div>
        ) : (
          <div className="space-y-2">
            {tasks.map((t) => (
              <div key={t.id} data-testid={`task-${t.id}`} className={`flex items-center gap-3 rounded-xl border border-zinc-800 bg-black/40 p-3 ${t.statut === "fait" ? "opacity-55" : ""}`}>
                <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${t.statut === "fait" ? "bg-emerald-500 border-emerald-500" : "border-zinc-600 hover:border-amber-400"}`}>
                  {t.statut === "fait" && <Check size={13} className="text-black" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${t.statut === "fait" ? "line-through text-zinc-500" : "text-zinc-200"}`}>{t.titre}</div>
                  {t.lien && <button onClick={() => nav(t.lien)} className="text-[11px] text-sky-400 hover:underline">{t.lien}</button>}
                </div>
                <select value={t.priorite} onChange={(e) => setPrio(t, e.target.value)} className={`text-[10px] rounded border px-1.5 py-1 bg-transparent ${PRIO[t.priorite]?.cls || ""}`} data-testid={`task-prio-${t.id}`}>
                  <option value="haute">Haute</option><option value="moyenne">Moyenne</option><option value="basse">Basse</option>
                </select>
                <button onClick={() => del(t.id)} className="text-zinc-500 hover:text-red-400 shrink-0" data-testid={`task-del-${t.id}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
